/**
 * Withdrawals Service for CoinVAT API
 * Handles withdrawal operations including wallet info, fee estimation, and withdrawal management
 */
import { apiClient } from './api-client';
import type {
  WithdrawalRequest,
  WithdrawalResponse,
  WithdrawalEstimate,
  WithdrawalHistory,
  WithdrawalLimitsResponse,
  WithdrawalVerification,
  TokenInfo,
  WalletInfo,
  WithdrawalHistoryParams,
} from './types';

export class WithdrawalsService {
  /**
   * Get user's auto-generated wallet information
   */
  async getWalletInfo(): Promise<WalletInfo> {
    return apiClient.get<WalletInfo>('/api/v1/withdrawals/wallet');
  }

  /**
   * Estimate withdrawal fee for a specific token and amount
   */
  async estimateWithdrawalFee(request: WithdrawalRequest): Promise<WithdrawalEstimate> {
    return apiClient.post<WithdrawalEstimate>('/api/v1/withdrawals/estimate', request);
  }

  /**
   * Request a new withdrawal
   */
  async requestWithdrawal(request: WithdrawalRequest): Promise<WithdrawalResponse> {
    return apiClient.post<WithdrawalResponse>('/api/v1/withdrawals/request', request);
  }

  /**
   * Verify and execute withdrawal using verification token
   */
  async verifyWithdrawal(
    withdrawalUuid: string, 
    verification: WithdrawalVerification
  ): Promise<WithdrawalResponse> {
    return apiClient.post<WithdrawalResponse>(
      `/api/v1/withdrawals/verify/${withdrawalUuid}`, 
      verification
    );
  }

  /**
   * Get user's withdrawal history with pagination
   */
  async getWithdrawalHistory(params: WithdrawalHistoryParams = {}): Promise<WithdrawalHistory> {
    const queryParams = new URLSearchParams();
    
    if (params.limit !== undefined) {
      queryParams.append('limit', params.limit.toString());
    }
    if (params.offset !== undefined) {
      queryParams.append('offset', params.offset.toString());
    }
    if (params.status) {
      queryParams.append('status', params.status);
    }

    const queryString = queryParams.toString();
    const endpoint = queryString ? `/api/v1/withdrawals/history?${queryString}` : '/api/v1/withdrawals/history';
    
    return apiClient.get<WithdrawalHistory>(endpoint);
  }

  /**
   * Get user's withdrawal limits and usage
   */
  async getWithdrawalLimits(): Promise<WithdrawalLimitsResponse> {
    return apiClient.get<WithdrawalLimitsResponse>('/api/v1/withdrawals/limits');
  }

  /**
   * Get withdrawal status and update confirmations
   */
  async getWithdrawalStatus(withdrawalUuid: string): Promise<WithdrawalResponse> {
    return apiClient.get<WithdrawalResponse>(`/api/v1/withdrawals/status/${withdrawalUuid}`);
  }

  /**
   * Get list of supported tokens for withdrawal
   */
  async getSupportedTokens(): Promise<TokenInfo[]> {
    return apiClient.get<TokenInfo[]>('/api/v1/withdrawals/supported-tokens');
  }

  // Utility methods

  /**
   * Check if withdrawal is complete
   */
  isWithdrawalComplete(withdrawal: WithdrawalResponse): boolean {
    return withdrawal.status === 'completed';
  }

  /**
   * Check if withdrawal is failed
   */
  isWithdrawalFailed(withdrawal: WithdrawalResponse): boolean {
    return withdrawal.status === 'failed' || withdrawal.status === 'cancelled';
  }

  /**
   * Check if withdrawal is pending
   */
  isWithdrawalPending(withdrawal: WithdrawalResponse): boolean {
    return withdrawal.status === 'pending' || withdrawal.status === 'verified' || withdrawal.status === 'processing';
  }

  /**
   * Format withdrawal amount for display
   */
  formatWithdrawalAmount(withdrawal: WithdrawalResponse): string {
    const amount = parseFloat(withdrawal.amount);
    return `${amount.toFixed(6)} ${withdrawal.token_symbol}`;
  }

  /**
   * Format withdrawal amount in USD for display
   */
  formatWithdrawalAmountUSD(withdrawal: WithdrawalResponse): string | null {
    if (!withdrawal.amount_usd) return null;
    const amountUSD = parseFloat(withdrawal.amount_usd);
    return `$${amountUSD.toFixed(2)}`;
  }

  /**
   * Format withdrawal fee for display
   */
  formatWithdrawalFee(withdrawal: WithdrawalResponse): string | null {
    const fee = withdrawal.actual_fee || withdrawal.estimated_fee;
    if (!fee) return null;
    const feeAmount = parseFloat(fee);
    return `${feeAmount.toFixed(6)} ETH`;
  }

  /**
   * Get withdrawal status display text
   */
  getWithdrawalStatusText(status: string): string {
    const statusMap: Record<string, string> = {
      'pending': 'Pending Verification',
      'verified': 'Verified',
      'processing': 'Processing',
      'completed': 'Completed',
      'failed': 'Failed',
      'cancelled': 'Cancelled',
    };
    return statusMap[status] || status;
  }

  /**
   * Check if token is supported for withdrawal
   */
  isTokenSupported(token: TokenInfo): boolean {
    return token.is_supported;
  }

  /**
   * Check if amount meets minimum withdrawal requirement
   */
  isAmountAboveMinimum(amount: string | number, token: TokenInfo): boolean {
    const amountNum = typeof amount === 'string' ? parseFloat(amount) : amount;
    const minimumNum = parseFloat(token.minimum_withdrawal);
    return amountNum >= minimumNum;
  }

  /**
   * Format token balance for display
   */
  formatTokenAmount(amount: string | number, symbol: string, decimals: number = 6): string {
    const amountNum = typeof amount === 'string' ? parseFloat(amount) : amount;
    return `${amountNum.toFixed(decimals)} ${symbol}`;
  }

  /**
   * Calculate withdrawal fee percentage
   */
  calculateFeePercentage(amount: string, fee: string): number {
    const amountNum = parseFloat(amount);
    const feeNum = parseFloat(fee);
    return (feeNum / amountNum) * 100;
  }

  /**
   * Get remaining withdrawal limits
   */
  getRemainingLimits(limits: WithdrawalLimitsResponse): {
    dailyETH: number;
    dailyUSD: number;
    monthlyETH: number;
    monthlyUSD: number;
  } {
    return {
      dailyETH: parseFloat(limits.daily_remaining_eth),
      dailyUSD: parseFloat(limits.daily_remaining_usd),
      monthlyETH: parseFloat(limits.monthly_remaining_eth),
      monthlyUSD: parseFloat(limits.monthly_remaining_usd),
    };
  }

  /**
   * Check if withdrawal amount is within limits
   */
  isWithinLimits(
    amount: string, 
    amountUSD: string | null, 
    limits: WithdrawalLimitsResponse
  ): { 
    withinDaily: boolean; 
    withinMonthly: boolean; 
    withinBoth: boolean; 
  } {
    const amountETH = parseFloat(amount);
    const amountUSDNum = amountUSD ? parseFloat(amountUSD) : 0;
    
    const dailyRemainingETH = parseFloat(limits.daily_remaining_eth);
    const dailyRemainingUSD = parseFloat(limits.daily_remaining_usd);
    const monthlyRemainingETH = parseFloat(limits.monthly_remaining_eth);
    const monthlyRemainingUSD = parseFloat(limits.monthly_remaining_usd);
    
    const withinDaily = amountETH <= dailyRemainingETH && (amountUSD === null || amountUSDNum <= dailyRemainingUSD);
    const withinMonthly = amountETH <= monthlyRemainingETH && (amountUSD === null || amountUSDNum <= monthlyRemainingUSD);
    
    return {
      withinDaily,
      withinMonthly,
      withinBoth: withinDaily && withinMonthly,
    };
  }
}

// Export singleton instance
export const withdrawalsService = new WithdrawalsService();